/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AdcsApiService,
    BackupApiService,
    NamespacesApiService,
    OrganisationsApiService,
    PortalRbacApiService,
    ResourcesApiService,
    ScheduleApiService,
    SecurityApiService
} from '../../http';
import { AdcModel, DashboardDataModel, OrganisationModel } from '../../models';
import { ApiService, LogService, SocketService, UserService, UtilsService } from '../../../../../core/services';
import { ErrorResponseModel, UserSessionModel } from '../../../../../core/models';
import { EventEmitter, Injectable } from '@angular/core';
import { NamespaceService } from '../utils';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class DashboardService {
    /**
     * Emit all data to all subscribers
     */
    public allDataEmitter: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Emit all data to all subscribers
     */
    public loadingEmitter: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Stored user data.
     */
    public storedUserData: UserSessionModel;

    /**
     * Store all errors.
     * @private
     */
    private _errors: object = {};

    /**
     * Store a list of error to be passed to the dashboard.
     * @private
     */
    private errorList: any = {};

    /**
     * Store all data.
     * @private
     */
    private _allData: DashboardDataModel = {
        adcs: [],
        backups: [],
        certs: [],
        insights: [],
        namespaces: [],
        organisation: {} as OrganisationModel,
        schedules: [],
        timeStamp: 0,
        users: [],
    };

    /**
     * Namespace selected key.
     * @private
     */
    private selectedNamespaceKey: number = 0;

    constructor(
        private adcsApiService: AdcsApiService,
        private apiService: ApiService,
        private backupApiService: BackupApiService,
        private logService: LogService,
        private namespaceApiService: NamespacesApiService,
        private namespaceService: NamespaceService,
        private organisationsApiService: OrganisationsApiService,
        private portalRbacApiService: PortalRbacApiService,
        private resourcesApiService: ResourcesApiService,
        private scheduleApiService: ScheduleApiService,
        private securityApiService: SecurityApiService,
        private socketService: SocketService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        this.selectedNamespaceKey = this.namespaceService.selectedNamespace?.key || 0;
        this.subscribeToServiceChanges();
        this.setupSocketListeners();
    }

    set errors(errors: any) {
        this._errors = errors;
    }

    get errors(): any {
        return this._errors;
    }

    get allData(): DashboardDataModel {
        return this._allData;
    }

    /**
     * Register api data.
     * @param data
     */
    set allData(data: DashboardDataModel) {
        if (JSON.stringify(this.allData) !== JSON.stringify(data)) {
            this._allData = data;
            this.allDataEmitter.next(data);
        }
    }

    /**
     * Get service data
     * @param getWidgetDataOnly
     */
    public getServiceData(getWidgetDataOnly: boolean = false): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.utilsService.debounce('getServiceData', (): void => {
                const user: any = this.utilsService.duplicateObject(this.userService.getUser());
                this.storedUserData = user;
                this.errorList = {}; // reset the error list on can activate, causes dashboard to not update.
                const apiCalls: any = this.getApiCalls(user, getWidgetDataOnly);

                forkJoin(apiCalls)
                    .pipe(map((result: any): any => result))
                    .subscribe({
                        next: (data: any): any => {
                            this.errors = this.errorList;
                            data['timeStamp'] = Date.now();
                            observer.next(data);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.errors = this.errorList;
                            this.logService.error('DashboardService: getServiceData', 'Unable to resolve page data ', error);
                            observer.error(error);
                            observer.complete();
                        }

                    });
            }, 500);
        });
    }

    /**
     * Get api calls.
     * @param user
     * @param getWidgetDataOnly
     * @private
     */
    private getApiCalls(user: any, getWidgetDataOnly: boolean = false): any {
        /* eslint-disable max-len */
        const apiCalls = {
            adcs: this.adcsApiService.getAll(this.selectedNamespaceKey).pipe(this.apiService.handleRequest(this.errorList, 'adcs').bind(this)),
            backups: this.backupApiService.getBackupsByNamespaceId(this.selectedNamespaceKey).pipe(this.apiService.handleRequest(this.errorList, 'backups').bind(this)),
            insights: this.securityApiService.getAllSecurity(this.selectedNamespaceKey).pipe(this.apiService.handleRequest(this.errorList, 'insights').bind(this)),
            // TODO waiting for backend team to fix the issue.
            // certs: this.securityApiService.getAllCertificates(this.selectedNamespaceKey).pipe(this.apiService.handleRequest(this.errorList, 'insights').bind(this)),
            namespaces: this.namespaceApiService.getAll(user.organisationId).pipe(this.apiService.handleRequest(this.errorList, 'namespaces').bind(this)),
            organisation: this.organisationsApiService.get(user.organisationId).pipe(this.apiService.handleRequest(this.errorList, 'organisation').bind(this)),
            schedules: this.scheduleApiService.getAllSchedules(this.selectedNamespaceKey).pipe(this.apiService.handleRequest(this.errorList, 'schedules').bind(this)),
            users: this.organisationsApiService.getMembers(user.organisationId).pipe(this.apiService.handleRequest(this.errorList, 'users').bind(this))
        };

        if (!getWidgetDataOnly) {
            apiCalls['rbac'] = this.portalRbacApiService.getUserEndpoints().pipe(this.apiService.handleRequest(this.errorList, 'rbac').bind(this));
            apiCalls['resources'] = this.resourcesApiService.getResources().pipe(this.apiService.handleRequest(this.errorList, 'resources').bind(this));
        }
        /* eslint-enable max-len */

        return apiCalls;
    }

    /**
     * Subscribe to service changes to keep counts up to date.
     * @private
     */
    private subscribeToServiceChanges(): void {
        this.namespaceService.selectedEmitter
            .subscribe({
                next: (namespace: any): void => {
                    if (this.selectedNamespaceKey !== namespace.key) {
                        this.setUpWidgetData();
                    }
                    this.selectedNamespaceKey = namespace.key;
                }
            });
    }

    /**
     * Set up widget data.
     * @private
     */
    private setUpWidgetData(): void {
        this.loadingEmitter.next(true);
        this.getServiceData(true)
            .subscribe({
                next: (data: any): void => {
                    this.allData = {
                        adcs: data.adcs,
                        backups: data.backups,
                        certs: data.certs,
                        insights: data.insights,
                        namespaces: data.namespaces,
                        organisation: data.organisation,
                        schedules: data.schedules,
                        timeStamp: Date.now(),
                        users: data.users
                    };
                    this.loadingEmitter.next(false);
                }
            });
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        let storedAddedADC: any = null;
        this.socketService.on('AdcAdd')
            .subscribe({
                next: (response: any): void => {
                    storedAddedADC = response.data;
                }
            });

        this.socketService.on('ADCStateChange')
            .subscribe({
                next: (response: any): void => {
                    // Make sure the state change is related to the ADC just added.
                    if (storedAddedADC.id === response.id && response.adoptionStatus === 'adopted') {
                        this.getAdcs();
                    }
                }
            });

        this.socketService.on('AdcRemove')
            .subscribe({
                next: (): void => {
                    this.getAdcs();
                }
            });
    }

    /**
     * Get ADCs.
     * @private
     */
    private getAdcs(): void {
        this.adcsApiService.getAll(this.selectedNamespaceKey)
            .subscribe({
                next: (data: AdcModel[]): void => {
                    this.updateDataItem('adcs', data);
                }
            });
    }

    /**
     * Update stored data.
     * @param item
     * @param value
     * @private
     */
    private updateDataItem(item: any, value: any): void {
        const dataItem: any = this._allData[item];
        if (dataItem) {
            this._allData[item] = value;
            this.allDataEmitter.next(this._allData);
        } else {
            this.logService.warn('DashboardService:updateDataItem', 'Can\'t update item ' + item);
        }
    }
}
